<template>
  <div v-if="canUpdate">
    <v-dialog
      v-if="device"
      v-model="showRebootDevice"
      max-width="640px"
    >
      <videon-card :heading="'Reboot ' + device.deviceName()" showClose="true" @close="showRebootDevice = false">
        <v-card-text>
          <p class="subtitle-1 my-5">
            Are you sure you want to apply the update and reboot <span class="primary--text">{{ device.deviceName() }}</span>?
          </p>
          <v-form @submit.prevent="rebootDevice">
            <v-text-field
              v-model="rebootDeviceConfirm"
              label="Reboot Confirmation"
              messages="Please type CONFIRM above to proceed"
              required
              outlined
            ></v-text-field>
            <div class="mt-3 d-flex align-end">
              <v-spacer />
              <v-btn
                class="ma-1"
                color="secondary"
                @click="showRebootDevice = false"
                text
              >
                Cancel
              </v-btn>
              <v-btn
                class="ma-1"
                color="primary"
                @click="applyUpdate"
              >
                Apply & Reboot
              </v-btn>
            </div>
          </v-form>
        </v-card-text>
      </videon-card>
    </v-dialog>
    
    <div v-if="!hasUpdates">
      <v-card
        color="info"
        outlined
        dark
        class="my-3"
      >
        <v-card-text>
          <h4 class="text-uppercase white--text d-inline-flex align-center"><v-icon color="white" class="mr-2">system_update_alt</v-icon> Firmware Update</h4>
          
          <device-update-state-rows :rows="updateStateDetailRows" row-key-prefix="ecm-none" />
          
          <v-alert
            text
            outlined
            color="disabled"
            icon="mdi-alert-outline"
            class="mt-3 mb-0"
            v-if="updateStatus === 'complete'"
          >
            {{ updateMessage }}
          </v-alert>
          
          <v-progress-linear
            class="mt-5 mb-2"
            color="white"
            indeterminate
            buffer-value="0"
            v-if="isUpdating"
          ></v-progress-linear>
          
          <v-btn @click="updateAction('check_for_update')" color="secondary" width="100%" class="mt-4" :disabled="disableUpdateButton">Check for Update</v-btn>
        </v-card-text>
      </v-card>
    </div>
    <div v-if="hasUpdates">
      <v-card
        color="info"
        outlined
        dark
        class="my-3"
      >
        <v-card-text>
          <h4 class="text-uppercase white--text d-inline-flex align-center"><v-icon color="white" class="mr-2">system_update_alt</v-icon> Firmware Update Available</h4>
          
          <device-update-state-rows :rows="updateStateDetailRows" row-key-prefix="ecm-yes" />
          
          <v-alert
            text
            outlined
            color="black"
            class="mt-3 mb-0"
            v-if="updateStatus === 'rejected' || updateStatus === 'error'"
          >
            {{ updateMessage }}.
          </v-alert>
          
          <v-alert
            text
            outlined
            color="disabled"
            icon="mdi-alert-outline"
            class="mt-3 mb-0"
            v-else-if="updateStatus === 'complete'"
          >
            {{ updateMessage }}
          </v-alert>
          
          <v-alert
            text
            outlined
            color="disabled"
            icon="mdi-alert-outline"
            class="mt-3 mb-0"
            v-else-if="device && !device.isOnline() && !isUpdating"
          >
            {{ version.original }} is available. However, you are unable to apply updates while the device is offline.
          </v-alert>
          <v-alert
            text
            outlined
            color="disabled"
            icon="mdi-alert-outline"
            class="mt-3 mb-0"
            v-else-if="device && !device.isOnline() && isUpdating"
          >
            The device is rebooting...
          </v-alert>
          
          <p class="mb-0 mt-2">
            <a href="https://support.videonlabs.com/hc/en-us/articles/360016605353-Device-Software-and-Release-Notes" target="_blank">Device Software and Release Notes</a>
          </p>
          
          <div v-if="device && device.isOnline()">
            <v-progress-linear
              class="mt-5 mb-2"
              color="white"
              :value="processPercent"
              buffer-value="0"
              v-if="showDeterminateDownloadProgress"
            ></v-progress-linear>
            
            <v-progress-linear
              class="mt-5 mb-2"
              color="white"
              indeterminate
              buffer-value="0"
              v-else-if="isUpdating"
            ></v-progress-linear>
            
            <v-btn
              v-if="device.firmwareApplyActive"
              color="secondary"
              width="100%"
              class="mt-4"
              disabled
            >
              Updating...
            </v-btn>
            
            <v-btn 
              v-else-if="showDeviceDownloadButton" 
              @click="updateAction('download_update')" 
              color="secondary" 
              width="100%" 
              class="mt-4" 
              :disabled="disableUpdateButton">
                {{ primaryDownloadButtonLabel }}
            </v-btn>
            
            <v-btn 
              v-else-if="showRebootPrompt" 
              @click="showRebootDevice = true" 
              color="red" 
              width="100%" 
              class="mt-4" 
              :disabled="disableUpdateButton">
                {{ rebootButtonLabel }}
            </v-btn>
            
            <v-btn 
              v-else-if="showAlreadyOnLatestCheckButton" 
              @click="updateAction('check_for_update')" 
              color="secondary" 
              width="100%" 
              class="mt-4" 
              :disabled="disableUpdateButton">
                Check for Update
            </v-btn>
            
            <p
              v-else-if="showDownloadingMessage"
              class="mb-0 mt-4 white--text"
            >
              {{ downloadingMessage }}
            </p>
            
            <v-btn 
              v-else
              color="secondary" 
              width="100%" 
              class="mt-4" 
              disabled>
              {{ systemUpdateStatusFallback }}
            </v-btn>
            
          </div>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script>
  import DeviceUpdateStateRows from './DeviceUpdateStateRows.vue'
  import { runDeviceFirmwareCommand } from './deviceFirmwareCommand.js'
  import firmwareUpdateContract from './firmwareUpdateContract.js'
  
  export default {
    name: 'DeviceFirmwareUpdaterEdgecaster_Max',
    components: {
      DeviceUpdateStateRows,
    },
    props: ['device'],
    
    data() {
      return {
        showRebootDevice: false,
        rebootDeviceConfirm: '',
        
        updateActionRequested: false,
      }
    },
    
    computed: {
      canUpdate() {
        if (this.device.state && this.device.state.update_state) {
          return true
        }
        return false
      },
      
      canEdit() {
        return this.device.canEdit()
      },
      
      // Prefer daemon over system when both have pending targets (same as Edgecaster).
      pendingFirmwareUpdate() {
        return this.device.updatesAvailable['daemon'] || this.device.updatesAvailable['system']
      },
      
      version() {
        return this.pendingFirmwareUpdate
      },
      
      hasUpdates() {
        return !!this.pendingFirmwareUpdate
      },
      
      daemonEntry() {
        if (this.device.state && this.device.state.update_state) {
          return this.$helpers.findUpdateStateEntry(this.device.state.update_state, 'daemon')
        }
        return null
      },
      
      systemEntry() {
        if (this.device.state && this.device.state.update_state) {
          return this.$helpers.findUpdateStateEntry(this.device.state.update_state, 'system')
        }
        return null
      },
      
      daemonUpdateStatus() {
        var daemonState = this.daemonEntry
        if (daemonState && daemonState.status != null && daemonState.status !== '') {
          return String(daemonState.status).toLowerCase()
        }
        return ''
      },
      
      systemUpdateAction() {
        var systemState = this.systemEntry
        if (systemState && systemState.action != null && systemState.action !== '') {
          return String(systemState.action).toLowerCase()
        }
        return ''
      },
      
      systemUpdateStatus() {
        var systemState = this.systemEntry
        if (systemState && systemState.status != null && systemState.status !== '') {
          return String(systemState.status).toLowerCase()
        }
        return ''
      },
      
      showAlreadyOnLatestCheckButton() {
        if (this.device.updatesAvailable['daemon'] || this.device.updatesAvailable['system']) {
          return false
        }
        var h = this.$helpers
        var dSt = this.daemonEntry ? h.normalizeUpdateStateStatus(this.daemonEntry.status) : ''
        var sSt = this.systemUpdateStatus
        return dSt === 'already on newer version' || dSt === 'already on newest version'
          || sSt === 'already on newer version' || sSt === 'already on newest version'
      },
      
      showDeviceDownloadButton() {
        var d = this.daemonEntry
        if (d && this.rowNeedsDownloadAction(d.status)) {
          return true
        }
        var s = this.systemEntry
        return !!(s && this.rowNeedsDownloadAction(s.status))
      },
      
      primaryDownloadButtonLabel() {
        var pd = this.device.updatesAvailable['daemon']
        var ps = this.device.updatesAvailable['system']
        var vo = this.version && this.version.original
        if (pd && ps) {
          return 'Download updates'
        }
        if (pd) {
          return vo ? ('Download daemon update (' + vo + ')') : 'Download daemon update'
        }
        if (ps) {
          return vo ? ('Download system ' + vo) : 'Download system update'
        }
        return 'Download update'
      },
      
      showDeterminateDownloadProgress() {
        return this.systemUpdateStatus === 'downloading update' || this.systemUpdateAction === 'apply'
      },
      
      showRebootPrompt() {
        if (this.device.firmwareApplyActive) {
          return false
        }
        var h = this.$helpers
        var d = this.daemonEntry
        if (d && h.updateStateStatusNeedsRebootPrompt(d.status)) {
          return true
        }
        var s = this.systemEntry
        return !!(s && h.updateStateStatusNeedsRebootPrompt(s.status))
      },
      
      rebootButtonLabel() {
        var d = this.device.updatesAvailable['daemon']
        var s = this.device.updatesAvailable['system']
        if (d && s) {
          return 'Apply updates & reboot'
        }
        if (d) {
          return 'Apply daemon update & reboot'
        }
        return 'Apply system update & reboot'
      },
      
      showDownloadingMessage() {
        if (this.showDeviceDownloadButton || this.showRebootPrompt || this.showAlreadyOnLatestCheckButton) {
          return false
        }
        var h = this.$helpers
        var d = this.daemonEntry
        var s = this.systemEntry
        if (d && h.updateStateStatusIndicatesInProgress(d.status)) {
          return true
        }
        return !!(s && h.updateStateStatusIndicatesInProgress(s.status))
      },
      
      downloadingMessage() {
        var d = this.device.updatesAvailable['daemon']
        var s = this.device.updatesAvailable['system']
        if (d && s) {
          return 'Downloading updates…'
        }
        if (d) {
          return 'Downloading daemon update…'
        }
        return 'Downloading system update…'
      },
      
      systemUpdateStatusFallback() {
        var parts = []
        if (this.daemonUpdateStatus) {
          parts.push('Daemon: ' + this.daemonUpdateStatus)
        }
        if (this.systemUpdateStatus) {
          parts.push('System: ' + this.systemUpdateStatus)
        }
        if (parts.length) {
          return parts.join(' · ')
        }
        return 'Preparing update…'
      },
      
      processPercent() {
        var systemState = this.systemEntry
        if (systemState && systemState.progress_percent != null) {
          return systemState.progress_percent
        }
        return 0
      },
      
      disableUpdateButton() {
        if (!this.canEdit || !this.device.isOnline() || this.device.isUpdating() || this.updateActionRequested) {
          return true
        }
        return false
      },
      
      isUpdating() {
        if (this.updateActionRequested) {
          return true
        }
        return this.device.isUpdating()
      },
      
      updateStatus() {
        return (this.device.command === 'update_device') ? this.device.command_status : false
      },
      
      updateMessage() {
        return (this.device.command === 'update_device') ? this.device.command_message : ''
      },
      
      updateStateDetailRows() {
        if (!this.device.state || !this.device.state.update_state) {
          return []
        }
        var h = this.$helpers
        var us = this.device.state.update_state
        var rows = h.updateStateRowsNotUpToDate(us, this.device.state, this.device.product_name).slice()
        var hasModuleRow = function (moduleName) {
          var m = String(moduleName).toLowerCase()
          return rows.some(function (r) {
            return r && r.module != null && String(r.module).toLowerCase() === m
          })
        }
        var state = this.device.state
        var product = this.device.product_name
        
        var daemon = h.findUpdateStateEntry(us, 'daemon')
        if (daemon && h.updateStateRowPendingForDevice(daemon, state, product) && !hasModuleRow('daemon')) {
          rows.unshift(daemon)
        }
        
        var system = h.findUpdateStateEntry(us, 'system')
        if (system && h.updateStateRowPendingForDevice(system, state, product) && !hasModuleRow('system')) {
          rows.unshift(system)
        }
        
        return rows
      },
    },
    watch: {
      systemUpdateStatus() {
        this.updateActionRequested = false
      },
      daemonUpdateStatus() {
        this.updateActionRequested = false
      },
    },
    methods: {
      rowNeedsDownloadAction(status) {
        if (status == null || status === '') {
          return false
        }
        var s = this.$helpers.normalizeUpdateStateStatus(status)
        return s === 'update available for download' || s === 'awaiting download'
      },
      
      applyUpdate() {
        if (this.rebootDeviceConfirm == 'CONFIRM') {
          this.showRebootDevice = false
          this.updateAction(firmwareUpdateContract.dedicatedRebootToUpdateCommand(
            this.device.product_name,
            this.device.state
          ))
          this.$emit('close')
        }
      },
      
      updateAction(command = 'download_update') {
        if (command !== 'check_for_update') {
          this.updateActionRequested = command
        }
        
        runDeviceFirmwareCommand(this, command).catch(function () {
          /* error state set on device */
        })
      }
    }
  }
</script>